import { useState, useEffect } from 'react';
import { useAssetDowntime, useStartAssetDowntime, useEndAssetDowntime, DOWNTIME_CATEGORY_LABELS, type DowntimeCategory } from '@/hooks/useAssetDowntime';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Power, PowerOff, Clock, History } from 'lucide-react';
import { format, formatDistanceStrict } from 'date-fns';

interface AssetDowntimeTabProps {
  assetId: string;
}

function formatDuration(minutes: number | null, fallbackStartIso?: string): string {
  if (minutes === null && fallbackStartIso) {
    return formatDistanceStrict(new Date(fallbackStartIso), new Date(), { addSuffix: false });
  }
  if (minutes === null) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function AssetDowntimeTab({ assetId }: AssetDowntimeTabProps) {
  const { data: events, isLoading } = useAssetDowntime(assetId);
  const { canEditAssets } = usePermissions();
  const startDowntime = useStartAssetDowntime();
  const endDowntime = useEndAssetDowntime();

  const active = events?.find((e) => e.ended_at === null) ?? null;
  const history = (events ?? []).filter((e) => e.ended_at !== null);

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState<DowntimeCategory>('failure');
  const [notes, setNotes] = useState('');

  // Live-tick the active duration banner
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [active]);

  const handleStart = () => {
    if (!reason.trim()) return;
    startDowntime.mutate(
      { asset_id: assetId, reason: reason.trim(), category, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setOpen(false);
          setReason('');
          setCategory('failure');
          setNotes('');
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active downtime banner */}
      {active ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <PowerOff className="h-4 w-4" />
              Out of Service
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Reason</p>
                <p className="font-medium">{active.reason}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Category</p>
                <Badge variant="outline">{DOWNTIME_CATEGORY_LABELS[active.category]}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Down for</p>
                <p className="font-medium">{formatDuration(null, active.started_at)}</p>
              </div>
            </div>
            {active.notes && (
              <p className="text-sm text-muted-foreground italic">"{active.notes}"</p>
            )}
            <p className="text-xs text-muted-foreground">
              Started {format(new Date(active.started_at), 'MMM d, yyyy h:mm a')}
            </p>
            {canEditAssets && (
              <Button
                variant="default"
                size="sm"
                onClick={() => endDowntime.mutate(active.id)}
                disabled={endDowntime.isPending}
              >
                <Power className="h-4 w-4 mr-2" />
                Return to Service
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        canEditAssets && (
          <Card>
            <CardContent className="pt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Asset is currently in service</p>
                  <p className="text-sm text-muted-foreground">
                    Open a downtime event if this asset becomes unavailable.
                  </p>
                </div>
              </div>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <PowerOff className="h-4 w-4 mr-2" />
                    Mark Out of Service
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Mark Asset Out of Service</DialogTitle>
                    <DialogDescription>
                      Records a downtime event. The asset's prior status will be restored when you return it to service.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label htmlFor="dt-reason">Reason</Label>
                      <Input
                        id="dt-reason"
                        placeholder="e.g., Hydraulic seal failure"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={category} onValueChange={(v) => setCategory(v as DowntimeCategory)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DOWNTIME_CATEGORY_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dt-notes">Notes (optional)</Label>
                      <Textarea
                        id="dt-notes"
                        placeholder="Additional context..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleStart} disabled={!reason.trim() || startDowntime.isPending}>
                      Mark Out of Service
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        )
      )}

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Downtime History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No prior downtime events recorded.
            </p>
          ) : (
            <div className="space-y-3">
              {history.map((e) => (
                <div key={e.id} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{e.reason}</span>
                        <Badge variant="outline" className="text-xs">
                          {DOWNTIME_CATEGORY_LABELS[e.category]}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(e.started_at), 'MMM d, yyyy h:mm a')}
                        {' → '}
                        {e.ended_at ? format(new Date(e.ended_at), 'MMM d, yyyy h:mm a') : 'open'}
                      </p>
                      {e.notes && (
                        <p className="text-sm text-muted-foreground mt-1 italic">"{e.notes}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                      <Clock className="h-3 w-3" />
                      {formatDuration(e.duration_minutes)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
